/**
 * EscrowPage - Escrow management for high-value collectibles
 * UK: Stripe Connect, Sri Lanka: Mark as received
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Shield, Lock, CheckCircle, Package, Truck, 
  AlertTriangle, Clock, DollarSign, FileText,
  ArrowRight, RefreshCw, XCircle, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Step Component
const StatusStep = ({ step, currentStatus, statusOrder }) => {
  const currentIndex = statusOrder.indexOf(currentStatus);
  const stepIndex = statusOrder.indexOf(step.status);
  const isComplete = stepIndex < currentIndex;
  const isCurrent = stepIndex === currentIndex;
  
  return (
    <div className={`flex items-center gap-3 p-4 rounded-lg ${
      isComplete ? 'bg-green-50' : isCurrent ? 'bg-blue-50' : 'bg-gray-50'
    }`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
        isComplete ? 'bg-green-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-300'
      }`}>
        {isComplete ? (
          <CheckCircle className="w-5 h-5 text-white" />
        ) : (
          <step.icon className="w-5 h-5 text-white" />
        )}
      </div>
      <div className="flex-1">
        <p className={`font-medium ${isComplete ? 'text-green-700' : isCurrent ? 'text-blue-700' : 'text-gray-500'}`}>
          {step.label}
        </p>
        <p className="text-sm text-gray-500">{step.description}</p>
      </div>
      {isCurrent && (
        <Badge className="bg-blue-500">Current</Badge>
      )}
    </div>
  );
};

export default function EscrowPage() {
  const { escrowId } = useParams();
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [escrow, setEscrow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  const isStripeConnect = country === 'UK';
  
  const statusOrder = ['pending', 'paid', 'shipped', 'delivered', 'confirmed', 'released'];
  
  const statusSteps = [
    { status: 'pending', label: 'Payment Pending', description: 'Waiting for buyer payment', icon: Clock },
    { status: 'paid', label: 'Payment Received', description: 'Funds held in escrow', icon: DollarSign },
    { status: 'shipped', label: 'Item Shipped', description: 'Seller has shipped the item', icon: Truck },
    { status: 'confirmed', label: 'Delivery Confirmed', description: 'Buyer confirmed receipt', icon: Package },
    { status: 'released', label: 'Funds Released', description: 'Payment sent to seller', icon: CheckCircle }
  ];
  
  // Fetch escrow details
  useEffect(() => {
    if (escrowId) {
      fetchEscrow();
    }
  }, [escrowId, country]);
  
  const fetchEscrow = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/books-media/escrow/${escrowId}?country=${country}`);
      if (!response.ok) throw new Error('Escrow not found');
      const data = await response.json();
      setEscrow(data);
    } catch (err) {
      toast.error('Failed to load escrow details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkShipped = async () => {
    if (!trackingNumber) {
      toast.error('Please enter tracking number');
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/books-media/escrow/${escrowId}/mark-shipped?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_number: trackingNumber, carrier })
      });
      
      toast.success('Item marked as shipped');
      fetchEscrow();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };
  
  const handleConfirmReceived = async () => {
    try {
      await fetch(`${API_URL}/api/books-media/escrow/${escrowId}/confirm-received?country=${country}`, {
        method: 'POST'
      });
      
      toast.success('Receipt confirmed! Funds will be released in 7 days.');
      fetchEscrow();
    } catch (err) {
      toast.error('Failed to confirm receipt');
    }
  };
  
  const handleDispute = async () => {
    if (!disputeReason) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/books-media/escrow/${escrowId}/dispute?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: disputeReason })
      });
      
      toast.success('Dispute created - our team will review');
      setShowDisputeModal(false);
      fetchEscrow();
    } catch (err) {
      toast.error('Failed to create dispute');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (!escrow) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Escrow Not Found</h2>
            <p className="text-gray-500 mb-4">The escrow transaction could not be found.</p>
            <Button onClick={() => navigate('/books-media')}>Back to Listings</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Escrow Protection</h1>
              <p className="text-white/80">
                {isStripeConnect ? 'Powered by Stripe Connect' : 'Mark as Received Protection'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-emerald-500" />
                  Transaction Status
                </CardTitle>
                <CardDescription>
                  Escrow #{escrow.id?.slice(-8).toUpperCase()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {escrow.status === 'disputed' ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      <strong>Dispute in Progress:</strong> {escrow.dispute_reason}
                    </AlertDescription>
                  </Alert>
                ) : (
                  statusSteps.map(step => (
                    <StatusStep 
                      key={step.status} 
                      step={step} 
                      currentStatus={escrow.status}
                      statusOrder={statusOrder}
                    />
                  ))
                )}
              </CardContent>
            </Card>
            
            {/* Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Seller: Mark as Shipped */}
                {escrow.status === 'paid' && (
                  <div className="p-4 border rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Mark as Shipped (Seller)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Tracking Number *</Label>
                        <Input
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          placeholder="e.g., 1Z999AA..."
                        />
                      </div>
                      <div>
                        <Label>Carrier</Label>
                        <Input
                          value={carrier}
                          onChange={(e) => setCarrier(e.target.value)}
                          placeholder="e.g., DHL, FedEx"
                        />
                      </div>
                    </div>
                    <Button onClick={handleMarkShipped}>
                      <Truck className="w-4 h-4 mr-2" />
                      Mark as Shipped
                    </Button>
                  </div>
                )}
                
                {/* Buyer: Confirm Receipt */}
                {escrow.status === 'shipped' && (
                  <div className="p-4 border rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Confirm Receipt (Buyer)
                    </h4>
                    <p className="text-sm text-gray-600">
                      By confirming, you acknowledge that you have received the item in the expected condition.
                      Funds will be released to the seller after 7 days.
                    </p>
                    <div className="flex gap-3">
                      <Button onClick={handleConfirmReceived} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirm Receipt
                      </Button>
                      <Button variant="outline" onClick={() => setShowDisputeModal(true)}>
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Report Issue
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Release info */}
                {escrow.status === 'confirmed' && escrow.release_date && (
                  <Alert>
                    <Clock className="w-4 h-4" />
                    <AlertDescription>
                      Funds will be automatically released to the seller on{' '}
                      <strong>{new Date(escrow.release_date).toLocaleDateString()}</strong>
                    </AlertDescription>
                  </Alert>
                )}
                
                {escrow.status === 'released' && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <AlertDescription className="text-green-700">
                      Transaction complete! Funds have been released to the seller.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Transaction Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Transaction Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-bold text-lg">{currencySymbol}{escrow.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Protection Type</span>
                  <Badge>{isStripeConnect ? 'Stripe Connect' : 'Standard'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span>{new Date(escrow.created_at).toLocaleDateString()}</span>
                </div>
                {escrow.tracking_number && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-500">Tracking Number</p>
                    <p className="font-mono">{escrow.tracking_number}</p>
                    {escrow.carrier && <p className="text-sm text-gray-500">{escrow.carrier}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Help Card */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Need Help?</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Our support team is here to help with any escrow questions.
                    </p>
                    <Button variant="link" className="px-0 mt-2">
                      Contact Support
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* How it works */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How Escrow Works</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <span>Buyer pays - funds held securely</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <span>Seller ships item with tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <span>Buyer confirms receipt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                    <span>After 7 days, funds released to seller</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Dispute Modal */}
      <Dialog open={showDisputeModal} onOpenChange={setShowDisputeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report an Issue</DialogTitle>
            <DialogDescription>
              Describe the problem with your order. Our team will review and help resolve it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>What's wrong?</Label>
              <Textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="e.g., Item not as described, damaged in shipping, wrong item received..."
                rows={4}
              />
            </div>
            
            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                Filing a dispute will pause the fund release until the issue is resolved.
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisputeModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDispute}>
              Submit Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
